/**
 * Envelope Migrations - StorageEnvelope version transitions.
 *
 * Registers all version steps for the StorageEnvelope<T> wrapper format.
 * See persistenceEngine.ts for the current envelope definition.
 *
 * v1 (current):
 *   { version: 1, checksum: string, savedAt: number, data: T }
 *
 * v0 (legacy, pre-envelope):
 *   Raw JSON without the envelope wrapper - handled in persistLoad as a
 *   legacy fallback. The v0→v1 migration formalizes this: wraps raw data
 *   in an envelope and computes a fresh checksum.
 *
 * ADDING A FUTURE MIGRATION:
 *   1. Define the new version shape
 *   2. Add a migration step: register(SCOPE_ENVELOPE, { fromVersion: N, toVersion: N+1, ... })
 *   3. Bump CURRENT_ENVELOPE_VERSION
 *   4. Add a test in migrationEngine.test.ts
 */

import { migrationRegistry, SCOPE_ENVELOPE } from "./migrationRegistry";

/** The envelope format version currently written by persistenceEngine. */
export const CURRENT_ENVELOPE_VERSION = 1;

/* =========================================================
   v0 → v1: Wrap legacy raw data in StorageEnvelope
   ========================================================= */

/**
 * Pre-envelope data has no version field.
 * This migration is invoked when persistLoad detects a pre-envelope payload.
 * It wraps the raw data in a valid v1 envelope.
 *
 * NOTE: The checksum is intentionally left as an empty string here.
 * The caller (persistenceEngine.persistLoad) recomputes the checksum
 * after the migration completes and before returning the data.
 */
migrationRegistry.register(SCOPE_ENVELOPE, {
  fromVersion:  0,
  toVersion:    1,
  description:  "Wrap legacy raw data in StorageEnvelope v1",
  migrate(rawData: unknown): { version: number; checksum: string; savedAt: number; data: unknown } {
    return {
      version:  1,
      checksum: "", // recomputed by persistLoad after migration
      savedAt:  Date.now(),
      data:     rawData,
    };
  },
  validate(result): boolean {
    return (
      typeof result === "object" &&
      result !== null &&
      "version" in result &&
      "data" in result &&
      (result as { version: number }).version === 1
    );
  },
});

/* =========================================================
   Future migrations registered here:
   migrationRegistry.register(SCOPE_ENVELOPE, { fromVersion: 1, toVersion: 2, ... })
   ========================================================= */
